/**
 * firestoreService.js
 * ===================
 * All Firestore read/write operations for the chemist editing system.
 *
 * Collections:
 *   users/{uid}             — role, displayName, email
 *   compound_edits/{id}     — every compound save event
 *   work_sessions/{id}      — login/logout session records
 */

import {
  collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc,
  query, orderBy, limit, where, serverTimestamp, onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';

// ── USER / ROLE ────────────────────────────────────────────────────────────

/** Fetch the user profile (role, name) from Firestore. */
export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data() : null;
}

/** Create or update a user profile. */
export async function upsertUserProfile(uid, data) {
  await setDoc(doc(db, 'users', uid), data, { merge: true });
}

// ── COMPOUND EDITS ────────────────────────────────────────────────────────

/**
 * Save a compound edit event.
 * @param {object} params
 *   uid          — editor's Firebase UID
 *   displayName  — editor's display name
 *   compoundId   — compound slug (e.g. "4-aco-met")
 *   compoundName — human name (e.g. "4-AcO-MET")
 *   fields       — { fieldPath: { before, after } } diff object
 *   sessionId    — current work session ID
 *   durationSec  — seconds spent on this compound since it was opened
 */
export async function saveCompoundEdit({
  uid, displayName, compoundId, compoundName,
  fields, sessionId, durationSec
}) {
  const ref = await addDoc(collection(db, 'compound_edits'), {
    uid,
    displayName,
    compoundId,
    compoundName,
    fields,
    sessionId: sessionId || null,
    durationSec: durationSec || 0,
    fieldCount: Object.keys(fields).length,
    timestamp: serverTimestamp(),
  });
  return ref.id;
}

/** Get recent edits, optionally filtered by uid or compoundId. */
export async function getRecentEdits({ uid, compoundId, limitN = 100 } = {}) {
  let q = query(
    collection(db, 'compound_edits'),
    orderBy('timestamp', 'desc'),
    limit(limitN)
  );
  if (uid) {
    q = query(
      collection(db, 'compound_edits'),
      where('uid', '==', uid),
      orderBy('timestamp', 'desc'),
      limit(limitN)
    );
  } else if (compoundId) {
    q = query(
      collection(db, 'compound_edits'),
      where('compoundId', '==', compoundId),
      orderBy('timestamp', 'desc'),
      limit(limitN)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Subscribe to real-time edit feed (for admin dashboard). Returns unsubscribe fn. */
export function subscribeToEdits(callback, limitN = 200) {
  const q = query(
    collection(db, 'compound_edits'),
    orderBy('timestamp', 'desc'),
    limit(limitN)
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// ── WORK SESSIONS ─────────────────────────────────────────────────────────

/** Start a new work session (call on login or first action). */
export async function startWorkSession({ uid, displayName }) {
  const ref = await addDoc(collection(db, 'work_sessions'), {
    uid,
    displayName,
    startedAt: serverTimestamp(),
    endedAt: null,
    durationSec: null,
    editCount: 0,
    compoundsTouched: [],
    active: true,
  });
  return ref.id;
}

/** End a work session (call on logout or tab close). */
export async function endWorkSession(sessionId, { durationSec, editCount, compoundsTouched }) {
  if (!sessionId) return;
  await updateDoc(doc(db, 'work_sessions', sessionId), {
    endedAt: serverTimestamp(),
    durationSec,
    editCount,
    compoundsTouched,
    active: false,
  });
}

/** Increment the edit count on an active session. */
export async function incrementSessionEdits(sessionId, compoundId) {
  if (!sessionId) return;
  const ref = doc(db, 'work_sessions', sessionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data();
  const touched = data.compoundsTouched || [];
  if (!touched.includes(compoundId)) touched.push(compoundId);
  await updateDoc(ref, {
    editCount: (data.editCount || 0) + 1,
    compoundsTouched: touched,
  });
}

/** Get recent sessions. */
export async function getRecentSessions({ uid, limitN = 50 } = {}) {
  let q;
  if (uid) {
    q = query(
      collection(db, 'work_sessions'),
      where('uid', '==', uid),
      orderBy('startedAt', 'desc'),
      limit(limitN)
    );
  } else {
    q = query(
      collection(db, 'work_sessions'),
      orderBy('startedAt', 'desc'),
      limit(limitN)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Subscribe to sessions in real time. */
export function subscribeToSessions(callback, limitN = 100) {
  const q = query(
    collection(db, 'work_sessions'),
    orderBy('startedAt', 'desc'),
    limit(limitN)
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// ── COMPOUND OVERRIDES (Firestore-backed, shared across users) ────────────

/**
 * Save a compound's full data override to Firestore.
 * This is the "permanent" counterpart to localStorage — visible to all users.
 */
export async function saveCompoundOverride(compoundId, fields, uid) {
  // Firestore setDoc with merge:true supports dot-notation keys for nested fields
  // e.g. { "pharmacology.halfLife": "4h" } merges correctly without wiping siblings
  await setDoc(
    doc(db, 'compound_overrides', compoundId),
    {
      ...fields,
      _updatedAt: serverTimestamp(),
      _updatedBy: uid,
    },
    { merge: true }
  );
}

/** Load ALL compound overrides from Firestore (call once at app start). */
export async function loadAllCompoundOverrides() {
  const snap = await getDocs(collection(db, 'compound_overrides'));
  const result = {};
  snap.docs.forEach(d => { result[d.id] = d.data(); });
  return result;
}

/** Subscribe to compound_overrides in real time. */
export function subscribeToOverrides(callback) {
  return onSnapshot(collection(db, 'compound_overrides'), snap => {
    const result = {};
    snap.docs.forEach(d => { result[d.id] = d.data(); });
    callback(result);
  });
}
